/**
 * Local Delivery - Custom Date Picker (calendar grid).
 * Replaces native <input type="date"> for reliable blackout/override/surcharge
 * marking and SGT timezone enforcement.
 *
 * API: window.WF_LD_DATE_PICKER.attach(inputEl, opts)
 *   opts: { minDate, maxDate, blackoutDates, surcharges, overrideDays, onSelect }
 *   Returns: { getValue, setValue, destroy }
 */
(function () {
  'use strict';

  var DATE = window.WF_LD_DATE_UTILS;
  if (!DATE) { console.warn('[wf-ld-date-picker] Missing WF_LD_DATE_UTILS'); return; }

  var CSS_INJECTED = false;
  function injectCSS() {
    if (CSS_INJECTED) return;
    CSS_INJECTED = true;
    var style = document.createElement('style');
    style.textContent = [
      '.wf-dp-wrap { position: relative; display: inline-block; }',
      '.wf-dp-display { cursor: pointer; background: #fff; border: 1px solid #d9d9d9; border-radius: 5px;',
      '  padding: 8px 10px; font-size: 14px; min-width: 180px; box-sizing: border-box; }',
      '.wf-dp-display:focus { outline: 2px solid #197bbd; outline-offset: -1px; }',
      '.wf-dp-popup { position: absolute; z-index: 1000; top: calc(100% + 4px); left: 0;',
      '  background: #fff; border: 1px solid #d9d9d9; border-radius: 8px; box-shadow: 0 4px 16px rgba(0,0,0,.12);',
      '  padding: 12px; width: 280px; display: none; }',
      '.wf-dp-popup.open { display: block; }',
      '.wf-dp-header { display: flex; align-items: center; justify-content: space-between; margin-bottom: 8px; }',
      '.wf-dp-title { font-size: 14px; font-weight: 600; color: #333; }',
      '.wf-dp-nav { background: none; border: 1px solid #d9d9d9; border-radius: 4px; width: 28px; height: 28px;',
      '  cursor: pointer; display: flex; align-items: center; justify-content: center; font-size: 14px; color: #545454; }',
      '.wf-dp-nav:hover { background: #f5f9fd; }',
      '.wf-dp-nav:disabled { opacity: .3; cursor: default; }',
      '.wf-dp-nav:disabled:hover { background: none; }',
      '.wf-dp-dow { display: grid; grid-template-columns: repeat(7, 1fr); text-align: center;',
      '  font-size: 11px; color: #999; margin-bottom: 4px; font-weight: 500; }',
      '.wf-dp-grid { display: grid; grid-template-columns: repeat(7, 1fr); gap: 2px; }',
      '.wf-dp-cell { position: relative; text-align: center; padding: 6px 0; font-size: 13px; border-radius: 4px;',
      '  cursor: pointer; user-select: none; color: #333; }',
      '.wf-dp-cell:hover { background: #f0f6fb; }',
      '.wf-dp-cell--empty { cursor: default; }',
      '.wf-dp-cell--empty:hover { background: none; }',
      '.wf-dp-cell--disabled { color: #ccc; cursor: default; }',
      '.wf-dp-cell--disabled:hover { background: none; }',
      '.wf-dp-cell--blackout { color: #c0392b; text-decoration: line-through; cursor: default; }',
      '.wf-dp-cell--blackout:hover { background: #fef5f5; }',
      '.wf-dp-cell--today { font-weight: 700; box-shadow: inset 0 0 0 1.5px #197bbd; }',
      '.wf-dp-cell--selected { background: #197bbd; color: #fff; font-weight: 600; }',
      '.wf-dp-cell--selected:hover { background: #1a6fa8; }',
      '.wf-dp-cell--override::after { content: ""; position: absolute; bottom: 2px; left: 50%;',
      '  transform: translateX(-50%); width: 4px; height: 4px; border-radius: 50%; background: #e67e22; }',
      '.wf-dp-cell--surcharge::before { content: "$"; position: absolute; top: 1px; right: 3px;',
      '  font-size: 8px; font-weight: 700; color: #e67e22; }',
      '.wf-dp-tooltip { position: absolute; z-index: 1010; bottom: calc(100% + 6px); left: 50%;',
      '  transform: translateX(-50%); background: #333; color: #fff; font-size: 12px; padding: 6px 10px;',
      '  border-radius: 5px; white-space: nowrap; pointer-events: auto; box-shadow: 0 2px 8px rgba(0,0,0,.18); }',
      '.wf-dp-tooltip::after { content: ""; position: absolute; top: 100%; left: 50%;',
      '  transform: translateX(-50%); border: 5px solid transparent; border-top-color: #333; }',
      '.wf-dp-tooltip a { color: #7ec8f0; text-decoration: underline; margin-left: 6px; }',
      '.wf-dp-tooltip a:hover { color: #fff; }',
    ].join('\n');
    document.head.appendChild(style);
  }

  var MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];
  var DOW = ['Su','Mo','Tu','We','Th','Fr','Sa'];

  function isoStr(y, m, d) {
    return y + '-' + String(m + 1).padStart(2, '0') + '-' + String(d).padStart(2, '0');
  }

  function parseIso(s) {
    if (!s) return null;
    var parts = s.split('-');
    return { y: parseInt(parts[0], 10), m: parseInt(parts[1], 10) - 1, d: parseInt(parts[2], 10) };
  }

  function buildSet(arr, key) {
    var set = {};
    if (!arr) return set;
    for (var i = 0; i < arr.length; i++) {
      var val = key ? arr[i][key] : arr[i];
      if (val) set[val] = arr[i];
    }
    return set;
  }

  function daysInMonth(y, m) {
    return new Date(y, m + 1, 0).getDate();
  }

  function firstDayOfMonth(y, m) {
    return new Date(y, m, 1).getDay();
  }

  function attach(inputEl, opts) {
    if (!inputEl) return null;
    opts = opts || {};
    injectCSS();

    var selected = '';
    var viewYear, viewMonth;
    var blackoutSet = buildSet(opts.blackoutDates, 'date');
    var surchargeSet = buildSet(opts.surcharges, 'date');
    var overrideSet = buildSet(opts.overrideDays, 'date');
    var minDate = opts.minDate || '';
    var maxDate = opts.maxDate || '';
    var onSelect = opts.onSelect || function () {};
    var displayPlaceholder = opts.displayPlaceholder || 'Select delivery date';
    var isDateDisabledFn = opts.isDateDisabled || null;

    var todayIso = DATE.todayStrSgt();
    var todayParsed = parseIso(todayIso);
    viewYear = todayParsed.y;
    viewMonth = todayParsed.m;

    var wrap = document.createElement('div');
    wrap.className = 'wf-dp-wrap';
    inputEl.parentNode.insertBefore(wrap, inputEl);

    var display = document.createElement('input');
    display.type = 'text';
    display.readOnly = true;
    display.className = 'wf-dp-display';
    display.placeholder = displayPlaceholder;
    display.setAttribute('aria-haspopup', 'true');
    wrap.appendChild(display);

    inputEl.type = 'hidden';
    wrap.appendChild(inputEl);

    var popup = document.createElement('div');
    popup.className = 'wf-dp-popup';
    popup.setAttribute('role', 'dialog');
    popup.setAttribute('aria-label', 'Date picker');
    wrap.appendChild(popup);

    function open() { popup.classList.add('open'); }
    function close() { popup.classList.remove('open'); }
    function isOpen() { return popup.classList.contains('open'); }

    display.addEventListener('click', function () {
      if (isOpen()) { close(); } else { render(); open(); }
    });
    display.addEventListener('keydown', function (e) {
      if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); display.click(); }
      if (e.key === 'Escape') close();
    });

    document.addEventListener('click', function (e) {
      if (!wrap.contains(e.target)) close();
    });

    function canNavPrev() {
      if (!minDate) return true;
      var lastDayPrev = new Date(viewYear, viewMonth, 0);
      var prevIso = isoStr(lastDayPrev.getFullYear(), lastDayPrev.getMonth(), lastDayPrev.getDate());
      return prevIso >= minDate;
    }

    function canNavNext() {
      if (!maxDate) return true;
      var firstNext = isoStr(viewYear, viewMonth + 1 > 11 ? 0 : viewMonth + 1, 1);
      if (viewMonth + 1 > 11) {
        firstNext = isoStr(viewYear + 1, 0, 1);
      }
      return firstNext <= maxDate;
    }

    function selectDate(iso) {
      selected = iso;
      inputEl.value = iso;
      inputEl.setAttribute('data-value', iso);
      display.value = DATE.formatDisplayDate(iso);
      close();
      onSelect(iso);
    }

    function render() {
      popup.innerHTML = '';

      var header = document.createElement('div');
      header.className = 'wf-dp-header';

      var prevBtn = document.createElement('button');
      prevBtn.type = 'button';
      prevBtn.className = 'wf-dp-nav';
      prevBtn.innerHTML = '&#x2039;';
      prevBtn.disabled = !canNavPrev();
      prevBtn.addEventListener('click', function (e) {
        e.stopPropagation();
        viewMonth--;
        if (viewMonth < 0) { viewMonth = 11; viewYear--; }
        render();
      });

      var title = document.createElement('span');
      title.className = 'wf-dp-title';
      title.textContent = MONTHS[viewMonth] + ' ' + viewYear;

      var nextBtn = document.createElement('button');
      nextBtn.type = 'button';
      nextBtn.className = 'wf-dp-nav';
      nextBtn.innerHTML = '&#x203A;';
      nextBtn.disabled = !canNavNext();
      nextBtn.addEventListener('click', function (e) {
        e.stopPropagation();
        viewMonth++;
        if (viewMonth > 11) { viewMonth = 0; viewYear++; }
        render();
      });

      header.appendChild(prevBtn);
      header.appendChild(title);
      header.appendChild(nextBtn);
      popup.appendChild(header);

      var dowRow = document.createElement('div');
      dowRow.className = 'wf-dp-dow';
      for (var d = 0; d < 7; d++) {
        var dowCell = document.createElement('span');
        dowCell.textContent = DOW[d];
        dowRow.appendChild(dowCell);
      }
      popup.appendChild(dowRow);

      var grid = document.createElement('div');
      grid.className = 'wf-dp-grid';
      var totalDays = daysInMonth(viewYear, viewMonth);
      var startDay = firstDayOfMonth(viewYear, viewMonth);

      for (var blank = 0; blank < startDay; blank++) {
        var emptyCell = document.createElement('div');
        emptyCell.className = 'wf-dp-cell wf-dp-cell--empty';
        grid.appendChild(emptyCell);
      }

      for (var day = 1; day <= totalDays; day++) {
        var iso = isoStr(viewYear, viewMonth, day);
        var cell = document.createElement('div');
        cell.className = 'wf-dp-cell';
        cell.textContent = String(day);
        cell.setAttribute('data-iso', iso);

        var outOfRange = (minDate && iso < minDate) || (maxDate && iso > maxDate);
        var isBlackout = !!blackoutSet[iso];
        var noSlots = !outOfRange && !isBlackout && isDateDisabledFn ? isDateDisabledFn(iso) : false;
        var isOverride = !!overrideSet[iso];
        var isSurcharge = !!surchargeSet[iso];
        var isSelected = iso === selected;
        var isToday = iso === todayIso;
        var isDisabled = outOfRange || isBlackout || noSlots;

        if (outOfRange || noSlots) cell.classList.add('wf-dp-cell--disabled');
        if (noSlots) cell.title = 'No timeslots available';
        if (isBlackout && !outOfRange) cell.classList.add('wf-dp-cell--blackout');
        if (isOverride && !isDisabled) cell.classList.add('wf-dp-cell--override');
        if (isSurcharge && !isDisabled) cell.classList.add('wf-dp-cell--surcharge');
        if (isToday) cell.classList.add('wf-dp-cell--today');
        if (isSelected) cell.classList.add('wf-dp-cell--selected');

        if (isBlackout && !outOfRange) {
          var bEntry = blackoutSet[iso];
          var reason = bEntry.reason;
          var bUrl = bEntry.url || '';
          if (bUrl) {
            (function (r, u, c) {
              var tip = null;
              c.addEventListener('mouseenter', function () {
                tip = document.createElement('div');
                tip.className = 'wf-dp-tooltip';
                tip.innerHTML = (r || 'Unavailable');
                var link = document.createElement('a');
                link.href = u;
                link.textContent = 'Shop collection';
                tip.appendChild(link);
                c.style.position = 'relative';
                c.appendChild(tip);
              });
              c.addEventListener('mouseleave', function () {
                if (tip && tip.parentNode) tip.parentNode.removeChild(tip);
                tip = null;
              });
            })(reason, bUrl, cell);
          } else {
            if (reason) cell.title = 'Unavailable: ' + reason;
            else cell.title = 'Unavailable';
          }
        }
        if (isSurcharge && !isDisabled) {
          cell.title = (cell.title ? cell.title + ' | ' : '') +
            'Surcharge: $' + surchargeSet[iso].amount +
            (surchargeSet[iso].reason ? ' (' + surchargeSet[iso].reason + ')' : '');
        }

        if (!isDisabled) {
          (function (isoVal) {
            cell.addEventListener('click', function (e) {
              e.stopPropagation();
              selectDate(isoVal);
            });
          })(iso);
        }

        grid.appendChild(cell);
      }

      popup.appendChild(grid);
    }

    function getValue() { return selected; }

    function setValue(iso) {
      if (!iso) {
        selected = '';
        inputEl.value = '';
        inputEl.setAttribute('data-value', '');
        display.value = '';
        return;
      }
      selected = iso;
      inputEl.value = iso;
      inputEl.setAttribute('data-value', iso);
      display.value = DATE.formatDisplayDate(iso);
      var p = parseIso(iso);
      if (p) { viewYear = p.y; viewMonth = p.m; }
    }

    function updateOpts(newOpts) {
      if (newOpts.minDate !== undefined) minDate = newOpts.minDate;
      if (newOpts.maxDate !== undefined) maxDate = newOpts.maxDate;
      if (newOpts.blackoutDates !== undefined) blackoutSet = buildSet(newOpts.blackoutDates, 'date');
      if (newOpts.surcharges !== undefined) surchargeSet = buildSet(newOpts.surcharges, 'date');
      if (newOpts.overrideDays !== undefined) overrideSet = buildSet(newOpts.overrideDays, 'date');
      if (newOpts.isDateDisabled !== undefined) isDateDisabledFn = newOpts.isDateDisabled;
      if (newOpts.displayPlaceholder !== undefined) {
        displayPlaceholder = newOpts.displayPlaceholder || 'Select delivery date';
        display.placeholder = displayPlaceholder;
      }
      if (isOpen()) render();
    }

    function navigateToMonth(year, month) {
      viewYear = year;
      viewMonth = month;
      if (isOpen()) render();
    }

    function destroy() {
      if (wrap.parentNode) {
        inputEl.type = 'date';
        wrap.parentNode.insertBefore(inputEl, wrap);
        wrap.parentNode.removeChild(wrap);
      }
    }

    return { getValue: getValue, setValue: setValue, destroy: destroy, updateOpts: updateOpts, navigateToMonth: navigateToMonth };
  }

  window.WF_LD_DATE_PICKER = { attach: attach };
})();
